import { FORM_CONSTANTS } from './patientFormConfig'

function sanitizeDigits(value, maxLength) {
  const digitsOnly = `${value ?? ''}`.replace(/\D/g, '')
  if (!Number.isFinite(maxLength) || maxLength <= 0) {
    return digitsOnly
  }
  return digitsOnly.slice(0, maxLength)
}

function toIntegerOrNull(value) {
  if (value === null || value === undefined || value === '') {
    return null
  }

  const numericValue = Number.parseInt(`${value}`, 10)
  return Number.isNaN(numericValue) ? null : numericValue
}

export const AGE_ENTRY_MODE_AGE = 'age'
export const AGE_ENTRY_MODE_DOB = 'dob'

export function getNormalizedAgeEntryMode(mode) {
  return mode === AGE_ENTRY_MODE_DOB ? AGE_ENTRY_MODE_DOB : AGE_ENTRY_MODE_AGE
}

/** `YYYY-MM-DD` for a valid ISO-ish date string / Date, else ''. */
export function sanitizeDateOfBirthInput(value) {
  if (!value) return ''
  const text = value instanceof Date ? value.toISOString() : `${value}`
  const match = text.trim().match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!match) return ''
  const candidate = `${match[1]}-${match[2]}-${match[3]}`
  const parsed = new Date(`${candidate}T00:00:00`)
  if (Number.isNaN(parsed.getTime())) return ''
  // Reject things like 2024-02-31 that Date silently rolls over.
  if (parsed.getMonth() + 1 !== Number(match[2]) || parsed.getDate() !== Number(match[3])) return ''
  return candidate
}

export function getTodayDateInputValue() {
  const today = new Date()
  const month = `${today.getMonth() + 1}`.padStart(2, '0')
  const day = `${today.getDate()}`.padStart(2, '0')
  return `${today.getFullYear()}-${month}-${day}`
}

export function getDateOfBirthValidationMessage(dobProvided) {
  const cleaned = sanitizeDateOfBirthInput(dobProvided)
  if (!cleaned) return 'Date of birth is required'
  if (cleaned > getTodayDateInputValue()) return 'Date of birth cannot be in the future'
  const detailed = getDetailedAgeFromDateOfBirth(cleaned)
  if (!detailed || detailed.years > FORM_CONSTANTS.MAX_AGE) {
    return `Years must be between ${FORM_CONSTANTS.MIN_AGE} and ${FORM_CONSTANTS.MAX_AGE}`
  }
  return null
}

function getPreviousMonthDays(referenceDate) {
  return new Date(referenceDate.getFullYear(), referenceDate.getMonth(), 0).getDate()
}

export function getNormalizedAgeSecondaryUnit(unit) {
  return unit === FORM_CONSTANTS.AGE_SECONDARY_UNIT_DAYS
    ? FORM_CONSTANTS.AGE_SECONDARY_UNIT_DAYS
    : FORM_CONSTANTS.AGE_SECONDARY_UNIT_MONTHS
}

export function sanitizeAgeYearsInput(value) {
  return sanitizeDigits(value, FORM_CONSTANTS.AGE_INPUT_MAX_LENGTH)
}

export function sanitizeAgeSecondaryValueInput(value) {
  return sanitizeDigits(value, FORM_CONSTANTS.AGE_SECONDARY_INPUT_MAX_LENGTH)
}

export function buildPatientAgeFields({
  ageYears,
  ageSecondaryValue,
  ageSecondaryUnit,
} = {}) {
  const normalizedYears = sanitizeAgeYearsInput(ageYears)
  const normalizedSecondaryValue = sanitizeAgeSecondaryValueInput(ageSecondaryValue)
  const normalizedSecondaryUnit = getNormalizedAgeSecondaryUnit(ageSecondaryUnit)

  return {
    age: normalizedYears === '' ? null : toIntegerOrNull(normalizedYears),
    ageYears: normalizedYears,
    ageSecondaryValue: normalizedSecondaryValue,
    ageSecondaryUnit: normalizedSecondaryUnit,
  }
}

export function getPatientAgeInputSnapshot(patientDetails = {}) {
  const normalizedYears = sanitizeAgeYearsInput(
    patientDetails?.ageYears ?? patientDetails?.age ?? '',
  )
  const normalizedSecondaryValue = sanitizeAgeSecondaryValueInput(
    patientDetails?.ageSecondaryValue ?? '',
  )
  const normalizedSecondaryUnit = getNormalizedAgeSecondaryUnit(patientDetails?.ageSecondaryUnit)

  return {
    yearsText: normalizedYears,
    years: toIntegerOrNull(normalizedYears),
    secondaryValueText: normalizedSecondaryValue,
    secondaryValue: toIntegerOrNull(normalizedSecondaryValue),
    secondaryUnit: normalizedSecondaryUnit,
  }
}

export function getPatientAgeValidationMessage(patientDetails = {}) {
  if (getNormalizedAgeEntryMode(patientDetails?.ageEntryMode) === AGE_ENTRY_MODE_DOB) {
    return getDateOfBirthValidationMessage(patientDetails?.dobProvided)
  }

  const { yearsText, years, secondaryValueText, secondaryValue, secondaryUnit } =
    getPatientAgeInputSnapshot(patientDetails)

  if (yearsText === '') {
    return 'Age is required'
  }

  if (
    years === null ||
    years < FORM_CONSTANTS.MIN_AGE ||
    years > FORM_CONSTANTS.MAX_AGE
  ) {
    return `Years must be between ${FORM_CONSTANTS.MIN_AGE} and ${FORM_CONSTANTS.MAX_AGE}`
  }

  if (secondaryValueText === '') {
    return null
  }

  if (secondaryValue === null || secondaryValue < 0) {
    return secondaryUnit === FORM_CONSTANTS.AGE_SECONDARY_UNIT_DAYS
      ? `Days must be between ${FORM_CONSTANTS.MIN_AGE_DAYS} and ${FORM_CONSTANTS.MAX_AGE_DAYS}`
      : `Months must be between ${FORM_CONSTANTS.MIN_AGE_MONTHS} and ${FORM_CONSTANTS.MAX_AGE_MONTHS}`
  }

  if (
    secondaryUnit === FORM_CONSTANTS.AGE_SECONDARY_UNIT_MONTHS &&
    secondaryValue > FORM_CONSTANTS.MAX_AGE_MONTHS
  ) {
    return `Months must be between ${FORM_CONSTANTS.MIN_AGE_MONTHS} and ${FORM_CONSTANTS.MAX_AGE_MONTHS}`
  }

  if (
    secondaryUnit === FORM_CONSTANTS.AGE_SECONDARY_UNIT_DAYS &&
    secondaryValue > FORM_CONSTANTS.MAX_AGE_DAYS
  ) {
    return `Days must be between ${FORM_CONSTANTS.MIN_AGE_DAYS} and ${FORM_CONSTANTS.MAX_AGE_DAYS}`
  }

  if (years === FORM_CONSTANTS.MAX_AGE && secondaryValue > 0) {
    return `Years must be between ${FORM_CONSTANTS.MIN_AGE} and ${FORM_CONSTANTS.MAX_AGE}`
  }

  return null
}

export function hasValidPatientAgeInput(patientDetails = {}) {
  return !getPatientAgeValidationMessage(patientDetails)
}

/** `YYYY-MM-DD 00:00:00` for the actual date of birth when the form is in DOB mode, else null. */
export function buildProvidedDateOfBirth(patientDetails = {}) {
  if (getNormalizedAgeEntryMode(patientDetails?.ageEntryMode) !== AGE_ENTRY_MODE_DOB) {
    return null
  }
  const cleaned = sanitizeDateOfBirthInput(patientDetails?.dobProvided)
  return cleaned && !getDateOfBirthValidationMessage(cleaned) ? `${cleaned} 00:00:00` : null
}

/**
 * `dateOfBirth` is always filled for compatibility: the actual date when one was entered,
 * otherwise a date assumed from the age fields.
 */
export function buildDateOfBirthFromPatientAge(patientDetails = {}) {
  const provided = buildProvidedDateOfBirth(patientDetails)
  if (provided) {
    return provided
  }

  if (!hasValidPatientAgeInput(patientDetails)) {
    return null
  }

  const { years, secondaryValue, secondaryUnit } = getPatientAgeInputSnapshot(patientDetails)
  const dateOfBirth = new Date()
  dateOfBirth.setHours(0, 0, 0, 0)
  dateOfBirth.setFullYear(dateOfBirth.getFullYear() - (years || 0))

  if (secondaryValue) {
    if (secondaryUnit === FORM_CONSTANTS.AGE_SECONDARY_UNIT_DAYS) {
      dateOfBirth.setDate(dateOfBirth.getDate() - secondaryValue)
    } else {
      dateOfBirth.setMonth(dateOfBirth.getMonth() - secondaryValue)
    }
  }

  return `${dateOfBirth.toISOString().split('T')[0]} 00:00:00`
}

export function getDetailedAgeFromDateOfBirth(dateOfBirth) {
  if (!dateOfBirth) {
    return null
  }

  const birthDate = new Date(`${dateOfBirth}`.substring(0, 10))
  if (Number.isNaN(birthDate.getTime())) {
    return null
  }

  const today = new Date()
  let years = today.getFullYear() - birthDate.getFullYear()
  let months = today.getMonth() - birthDate.getMonth()
  let days = today.getDate() - birthDate.getDate()

  if (days < 0) {
    months -= 1
    days += getPreviousMonthDays(today)
  }

  if (months < 0) {
    years -= 1
    months += 12
  }

  return {
    years: Math.max(0, years),
    months: Math.max(0, months),
    days: Math.max(0, days),
  }
}

export function buildPatientAgeFieldsFromDateOfBirth(dateOfBirth) {
  const detailedAge = getDetailedAgeFromDateOfBirth(dateOfBirth)
  if (!detailedAge) {
    return null
  }
  let ageSecondaryValue = ''
  let ageSecondaryUnit = FORM_CONSTANTS.AGE_SECONDARY_UNIT_MONTHS
  if (detailedAge.months > 0) {
    ageSecondaryValue = `${detailedAge.months}`
  } else if (detailedAge.days > 0) {
    ageSecondaryValue = `${detailedAge.days}`
    ageSecondaryUnit = FORM_CONSTANTS.AGE_SECONDARY_UNIT_DAYS
  }
  return buildPatientAgeFields({
    ageYears: `${detailedAge.years}`,
    ageSecondaryValue,
    ageSecondaryUnit,
  })
}

export function getPatientAgeFieldsFromPatientInfo(patientInfo = {}) {
  // An actual date of birth on the record puts the form in DOB mode; the age fields are
  // still derived from it so every reader of `age` / `ageYears` keeps working.
  const providedDob = sanitizeDateOfBirthInput(patientInfo?.dobProvided)
  if (providedDob) {
    return {
      ...(buildPatientAgeFieldsFromDateOfBirth(providedDob) ||
        buildPatientAgeFields({ ageYears: '', ageSecondaryValue: '', ageSecondaryUnit: '' })),
      ageEntryMode: AGE_ENTRY_MODE_DOB,
      dobProvided: providedDob,
    }
  }

  const detailedAge = getDetailedAgeFromDateOfBirth(patientInfo?.dateOfBirth)

  if (detailedAge) {
    let ageSecondaryValue = ''
    let ageSecondaryUnit = FORM_CONSTANTS.AGE_SECONDARY_UNIT_MONTHS

    if (detailedAge.years > 0) {
      if (detailedAge.months > 0) {
        ageSecondaryValue = `${detailedAge.months}`
        ageSecondaryUnit = FORM_CONSTANTS.AGE_SECONDARY_UNIT_MONTHS
      } else if (detailedAge.days > 0) {
        ageSecondaryValue = `${detailedAge.days}`
        ageSecondaryUnit = FORM_CONSTANTS.AGE_SECONDARY_UNIT_DAYS
      }
    } else if (detailedAge.months > 0) {
      ageSecondaryValue = `${detailedAge.months}`
      ageSecondaryUnit = FORM_CONSTANTS.AGE_SECONDARY_UNIT_MONTHS
    } else if (detailedAge.days > 0) {
      ageSecondaryValue = `${detailedAge.days}`
      ageSecondaryUnit = FORM_CONSTANTS.AGE_SECONDARY_UNIT_DAYS
    }

    return {
      ...buildPatientAgeFields({
        ageYears: `${detailedAge.years}`,
        ageSecondaryValue,
        ageSecondaryUnit,
      }),
      ageEntryMode: AGE_ENTRY_MODE_AGE,
      dobProvided: '',
    }
  }

  return {
    ...buildPatientAgeFields({
      ageYears: patientInfo?.age ?? '',
      ageSecondaryValue: patientInfo?.ageSecondaryValue ?? '',
      ageSecondaryUnit:
        patientInfo?.ageSecondaryUnit ?? FORM_CONSTANTS.DEFAULT_AGE_SECONDARY_UNIT,
    }),
    ageEntryMode: getNormalizedAgeEntryMode(patientInfo?.ageEntryMode),
    dobProvided: sanitizeDateOfBirthInput(patientInfo?.dobProvided),
  }
}

export function formatPatientAgeSummary(patientDetails = {}) {
  const { years, secondaryValue, secondaryUnit } = getPatientAgeInputSnapshot(patientDetails)

  if (years === null) {
    return null
  }

  const parts = [`${years}y`]
  if (secondaryValue !== null && secondaryValue > 0) {
    parts.push(
      secondaryUnit === FORM_CONSTANTS.AGE_SECONDARY_UNIT_DAYS
        ? `${secondaryValue}d`
        : `${secondaryValue}m`,
    )
  }

  return parts.join(' ')
}
